import {
  ACCENT_COLORS,
  type AccentColor,
  Box,
  Button,
  ColorSwatch,
  CONTRAST_PRESETS,
  DEFAULT_CONTRAST_PRESET,
  DEFAULT_SURFACE_PRESET,
  DEFAULT_THEME_APPEARANCE,
  type IconName,
  isAccentColor,
  isContrastPreset,
  isSurfacePreset,
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
  SURFACE_PRESETS,
  Text,
  type ThemeAppearance,
  useTheme,
} from "@salestrak/ui";
import React from "react";

import { useThemeAppearance } from "#app/components/theme/theme-appearance-provider";
import { useUserSettings } from "#app/lib/hooks/use-user-settings";

/**
 * The bare appearance controls, with no row or group chrome of their own.
 *
 * Two surfaces host them — the theme sub-menu (a 320px popup) and the
 * preferences screen (an 800px page) — and they want different framing: the
 * popup stacks the accent swatches under a caption, the page gives them a
 * label column like every other row on it. Exporting the controls rather than
 * finished rows is what lets each compose its own, and it keeps
 * `SettingsGroup`'s separator logic working, which counts direct children and
 * would see a fragment of three rows as one.
 */

export type ThemeMode = "light" | "dark" | "system";

export const THEME_MODE_OPTIONS: readonly {
  value: ThemeMode;
  label: string;
  icon: IconName;
}[] = [
  { value: "light", label: "Light", icon: "sun" },
  { value: "dark", label: "Dark", icon: "moon" },
  { value: "system", label: "Auto", icon: "monitor" },
];

/**
 * Light/dark/system, written to both the document and the user's preferences.
 *
 * The library's ThemeProvider owns the DOM class and its own localStorage
 * mirror; the preference is what carries the choice to another browser. Both
 * writes belong together, which is why no call site does one without the other.
 */
export function useThemeModePreference(): {
  mode: string;
  setMode: (mode: ThemeMode) => void;
} {
  const { theme, setTheme } = useTheme();
  const { updatePreference } = useUserSettings();

  const setMode = React.useCallback(
    (next: ThemeMode) => {
      setTheme(next);
      void updatePreference("themeMode", next);
    },
    [setTheme, updatePreference],
  );

  return { mode: theme, setMode };
}

interface AppearanceControlProps {
  /** The control column is sized by its host, not by the control. */
  width?: number;
}

/**
 * A Select that paints the option under the cursor for real, and puts the
 * user's own choice back if they leave without picking one.
 *
 * Every one of these controls changes the whole interface, so a swatch or a
 * name is a poor description of what it does — the page itself is the preview.
 * That is the same reason the restore has to be exact: the preview writes the
 * live attribute, so a menu dismissed without a choice would otherwise leave
 * the last hovered option applied.
 *
 * The provider already keeps the chosen appearance apart from the painted one,
 * so this reads `committed` for both the check mark — which stays on the real
 * choice while the hover walks the list — and the restore. The one thing it
 * has to hold itself is `chosenRef`, and it is a REF because `onValueChange`
 * and `onOpenChange` fire in the same tick: a state flag would still read
 * stale in the second handler, and the restore would undo the choice that had
 * just been made.
 */
function usePreviewedChoice<T extends string>(
  read: (appearance: ThemeAppearance) => T,
  apply: (base: ThemeAppearance, value: string) => ThemeAppearance | null,
): {
  value: T;
  onOpenChange: (open: boolean) => void;
  onValueChange: (value: unknown) => void;
  previewProps: (value: string) => {
    onMouseEnter: () => void;
    onFocus: () => void;
  };
} {
  const { committed, preview, commit } = useThemeAppearance();
  const chosenRef = React.useRef(false);

  const previewValue = (value: string) => {
    const next = apply(committed, value);
    if (next) preview(next);
  };

  return {
    value: read(committed),
    onOpenChange: (open: boolean) => {
      if (open) {
        chosenRef.current = false;
        return;
      }
      if (!chosenRef.current) preview(committed);
    },
    onValueChange: (value: unknown) => {
      if (typeof value !== "string") return;
      const next = apply(committed, value);
      if (!next) return;
      chosenRef.current = true;
      commit(next);
    },
    previewProps: (value: string) => ({
      onMouseEnter: () => previewValue(value),
      onFocus: () => previewValue(value),
    }),
  };
}

// The presets are ordered dimmest-to-brightest and least-to-most, and the
// labels are the preset names themselves — a stop that says "Bright" is the one
// thing a picker can promise, where a number on a slider promised a range whose
// ends were not reachable.
function title(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

const SURFACE_ITEMS = SURFACE_PRESETS.map((value) => ({
  value,
  label: title(value),
}));

const CONTRAST_ITEMS = CONTRAST_PRESETS.map((value) => ({
  value,
  label: title(value),
}));

/** How light the page and its panels sit. */
export function ThemeSurfaceSelect({
  width = 200,
}: AppearanceControlProps = {}) {
  const choice = usePreviewedChoice(
    (appearance) => appearance.surface,
    (base, value) =>
      isSurfacePreset(value) ? { ...base, surface: value } : null,
  );

  return (
    <Box width={width}>
      <Select
        items={SURFACE_ITEMS.map((item) => ({ ...item }))}
        value={choice.value}
        onOpenChange={choice.onOpenChange}
        onValueChange={choice.onValueChange}
      >
        <SelectTrigger size="sm" aria-label="Background lightness">
          <SelectValue />
        </SelectTrigger>
        <SelectPopup>
          {SURFACE_ITEMS.map((item) => (
            <SelectItem
              key={item.value}
              value={item.value}
              {...choice.previewProps(item.value)}
            >
              <Text size="sm">{item.label}</Text>
            </SelectItem>
          ))}
        </SelectPopup>
      </Select>
    </Box>
  );
}

/**
 * How hard text reads against whatever the surfaces settled on.
 *
 * These are contrast targets rather than a second lightness dial, which is why
 * the two controls cannot be combined into something illegible: the text is
 * solved against the surface, not moved independently of it.
 */
export function ThemeContrastSelect({
  width = 200,
}: AppearanceControlProps = {}) {
  const choice = usePreviewedChoice(
    (appearance) => appearance.contrast,
    (base, value) =>
      isContrastPreset(value) ? { ...base, contrast: value } : null,
  );

  return (
    <Box width={width}>
      <Select
        items={CONTRAST_ITEMS.map((item) => ({ ...item }))}
        value={choice.value}
        onOpenChange={choice.onOpenChange}
        onValueChange={choice.onValueChange}
      >
        <SelectTrigger size="sm" aria-label="Text contrast">
          <SelectValue />
        </SelectTrigger>
        <SelectPopup>
          {CONTRAST_ITEMS.map((item) => (
            <SelectItem
              key={item.value}
              value={item.value}
              {...choice.previewProps(item.value)}
            >
              <Text size="sm">{item.label}</Text>
            </SelectItem>
          ))}
        </SelectPopup>
      </Select>
    </Box>
  );
}

// The Select needs a string for "no accent"; `null` is not a value it can key
// an option by. `none` is what `tokens.css` names the shipped primary family,
// so the swatch has a real block to paint from.
const DEFAULT_ACCENT_VALUE = "none";

const ACCENT_SELECT_ITEMS: readonly { label: string; value: string }[] = [
  { label: "Default", value: DEFAULT_ACCENT_VALUE },
  ...ACCENT_COLORS.map((accent) => ({
    label: title(accent),
    value: accent,
  })),
];

// The dot paints from the accent's OWN block rather than from the live
// `--primary`, so an option names itself instead of following whatever is being
// previewed.
function accentDot(value: string): AccentColor | "none" {
  return isAccentColor(value) ? value : DEFAULT_ACCENT_VALUE;
}

function accentLabel(value: string): string {
  return (
    ACCENT_SELECT_ITEMS.find((item) => item.value === value)?.label ?? "Default"
  );
}

/**
 * Accent as a Select whose options carry their own color dot.
 *
 * The dot is what the other two controls have no equivalent of — a hue can be
 * named in a swatch, where "Bright" cannot — so this is the one of the three
 * that reads before it is previewed.
 */
export function ThemeAccentSelect({
  width = 200,
}: AppearanceControlProps = {}) {
  const choice = usePreviewedChoice(
    (appearance) => appearance.accent ?? DEFAULT_ACCENT_VALUE,
    (base, value) => ({
      ...base,
      accent: isAccentColor(value) ? value : null,
    }),
  );

  return (
    <Box width={width}>
      <Select
        items={ACCENT_SELECT_ITEMS.map((item) => ({ ...item }))}
        value={choice.value}
        onOpenChange={choice.onOpenChange}
        onValueChange={choice.onValueChange}
      >
        <SelectTrigger size="sm" aria-label="Accent color">
          <SelectValue>
            {(value) => (
              <Box direction="row" align="center" gap="sm">
                <ColorSwatch
                  accent={accentDot(String(value))}
                  size="sm"
                  rounded="full"
                />
                <Text size="sm">{accentLabel(String(value))}</Text>
              </Box>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectPopup>
          {ACCENT_SELECT_ITEMS.map((item) => (
            <SelectItem
              key={item.value}
              value={item.value}
              {...choice.previewProps(item.value)}
            >
              <Box direction="row" align="center" gap="sm">
                <ColorSwatch
                  accent={accentDot(item.value)}
                  size="sm"
                  rounded="full"
                />
                <Text size="sm">{item.label}</Text>
              </Box>
            </SelectItem>
          ))}
        </SelectPopup>
      </Select>
    </Box>
  );
}

/**
 * Whether the user has CHOSEN anything off the shipped design.
 *
 * Reads the committed appearance, never the painted one: hovering an option
 * repaints the page, and a reset affordance that appeared on hover would offer
 * to undo something that has not happened.
 */
export function useIsAppearanceCustomized(): boolean {
  const { committed } = useThemeAppearance();
  return (
    committed.accent !== null ||
    committed.surface !== DEFAULT_SURFACE_PRESET ||
    committed.contrast !== DEFAULT_CONTRAST_PRESET
  );
}

export function ThemeResetButton() {
  const { commit } = useThemeAppearance();

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => commit(DEFAULT_THEME_APPEARANCE)}
    >
      Reset appearance
    </Button>
  );
}
